import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { ContentLayout } from '@/layouts/ContentLayout';
import { PillButton } from '@/components/ui/PillButton';
import { setCachedTournamentRole } from '@/utils/tournamentRoleCache';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { normalizeTournaments } from '@/utils/normalizeTournaments';
import { TOURNAMENT_SOURCE_PATH, tournamentSourceFromPath } from '@/utils/tournamentListParams';
import { TournamentsListLayoutContext } from './context';
import type { TournamentsListLayoutContextType, TournamentsTab } from './context';
import MyTournamentsTab from '@/pages/tournaments-list/MyTournamentsTab';
import PublicTournamentsTab from '@/pages/tournaments-list/PublicTournamentsTab';
import ArchivedTournamentsTab from '@/pages/tournaments-list/ArchivedTournamentsTab';
import styles from '@/pages/TournamentsPage.module.scss';



// Plus icon for create button
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TournamentsListLayout: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useBreakpointDown('lg');
  const { t } = useTranslation(['tournament', 'common']);

  const activeTab: TournamentsTab = tournamentSourceFromPath(location.pathname);

  // Legacy (unpaginated) lists: "my" pre-warms tournamentRoleCache and feeds the
  // desktop sidebar; public / archived feed only the desktop sidebar of their tab.
  // Tabs fetch their own paginated lists (hooks/useTournamentList).
  const {
    data: myTournamentsRaw,
    isLoading: myLoading,
  } = useQuery({
    queryKey: ['my-tournaments', user?.id],
    queryFn: async () => {
      const response = await apiClient.getTournaments();
      return response.data?.data || [];
    },
    enabled: !!user?.id,
  });

  const {
    data: publicTournamentsRaw,
    isLoading: publicLoading,
  } = useQuery({
    queryKey: ['public-tournaments'],
    queryFn: async () => {
      const response = await apiClient.getPublicTournaments();
      return response.data?.data || [];
    },
    enabled: !isMobile && activeTab === 'public',
  });

  const {
    data: archivedTournamentsRaw,
    isLoading: archivedLoading,
  } = useQuery({
    queryKey: ['archived-tournaments', user?.id],
    queryFn: async () => {
      const response = await apiClient.getArchivedTournaments();
      return response.data?.data || [];
    },
    enabled: !!user?.id && !isMobile && activeTab === 'archived',
  });

  const myTournaments = useMemo(() => normalizeTournaments(myTournamentsRaw), [myTournamentsRaw]);
  const publicTournaments = useMemo(() => normalizeTournaments(publicTournamentsRaw), [publicTournamentsRaw]);
  const archivedTournaments = useMemo(() => normalizeTournaments(archivedTournamentsRaw), [archivedTournamentsRaw]);

  // Pre-warm tournamentRoleCache from "my tournaments" so that on the very first navigation
  // to /tournaments/:id, getInitialConfig can resolve nav visibility synchronously and avoid
  // the 2→4 expansion flicker for privileged users.
  useEffect(() => {
    if (!Array.isArray(myTournamentsRaw)) return;
    myTournamentsRaw.forEach((t: any) => {
      const role = t?.userRole || t?.user_role;
      if (t?.id && role) {
        setCachedTournamentRole(t.id, role);
      }
    });
  }, [myTournamentsRaw]);

  // Page config: dynamic title based on active tab
  const pageTitle = activeTab === 'my'
    ? t('tournament:myTournaments')
    : activeTab === 'public'
      ? t('tournament:publicTournaments')
      : t('tournament:archivedTournaments');

  usePageConfig({
    title: pageTitle,
    headerSolid: true,
    headerSearch: { placeholder: t('tournament:list.searchPlaceholder') },
    hideLanguageSwitcher: true,
  });

  const tabs = useMemo(() => [
    { component: MyTournamentsTab, path: TOURNAMENT_SOURCE_PATH.my },
    { component: PublicTournamentsTab, path: TOURNAMENT_SOURCE_PATH.public },
    { component: ArchivedTournamentsTab, path: TOURNAMENT_SOURCE_PATH.archived },
  ], []);

  const contextValue: TournamentsListLayoutContextType = useMemo(() => ({
    activeTab,
    isMobile,
  }), [activeTab, isMobile]);

  // Tournaments shown in sidebar depend on active tab
  const sidebarTournaments = activeTab === 'my'
    ? myTournaments
    : activeTab === 'public'
      ? publicTournaments
      : archivedTournaments;
  const sidebarLoading = activeTab === 'my'
    ? myLoading
    : activeTab === 'public'
      ? publicLoading
      : archivedLoading;

  // Sidebar content
  const sidebar = (
    <>
      <PillButton
        to="/tournaments/create"
        label={t('tournament:createTournament')}
        icon={<PlusIcon />}
        className={styles.createBtn}
      />

      <div className={styles.tournamentList}>
        <h3 className={styles.tournamentListTitle}>{t('tournament:quickAccess')}</h3>
        <div className={`${styles.tournamentListItems} ${sidebarTournaments.length > 4 ? styles.hasOverflow : ''}`}>
          <div className={styles.tournamentListTrack}>
            {sidebarLoading ? null : sidebarTournaments.length === 0 ? (
              <div className={styles.tournamentListEmpty}>
                {t('tournament:noTournamentsFoundSidebar')}
              </div>
            ) : (
              sidebarTournaments.map((tournament, index) => (
                <Link
                  key={tournament.id}
                  to={`/tournaments/${tournament.id}`}
                  className={styles.clubCard}
                >
                  <span className={styles.clubCardNum}>{index + 1}</span>
                  {tournament.listStatus && (
                    <StatusBadge status={tournament.listStatus} variant="soft" className={styles.clubCardBadge} />
                  )}
                  <div className={styles.clubCardInfo}>
                    <div className={styles.clubCardName}>{tournament.name}</div>
                    <div className={styles.clubCardMeta}>
                      {tournament.matchesCount} {t('tournament:list.matchesLabel', { count: tournament.matchesCount })}
                      {' • '}
                      {tournament.participantsCount} {t('tournament:list.participantsLabel', { count: tournament.participantsCount })}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <TournamentsListLayoutContext.Provider value={contextValue}>
      <ContentLayout
        sidebar={sidebar}
        tabs={tabs}
      />
    </TournamentsListLayoutContext.Provider>
  );
};

export default TournamentsListLayout;
